import { Close, PercentOutlined } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import {
  usePostFolioRemoveDiscountMutation,
  usePostFolioZeroRatedMutation,
  usePostModifyServiceChargeMutation,
} from '../../../../../api/front-office/roomposting.api.rtk';
import { getColor } from '../../../../../assets/theme';
import LoadingComponent from '../../../../../components/animation/loading-component';
import useToast from '../../../../../hooks/useToast';
import { useRoomPostingContext } from '../../../containers/fo-room_posting.container';
import {
  RoomPostingModifyServiceCharge,
  RoomPostingRemoveDiscZero,
} from '../../../interface/room-posting-interface';

import ContainerTitle from '@components/ui/container-title';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import useAppContext from '../../../../../hooks/useAppContext';
import useInformationDialog from '../../../../../hooks/useInformationDialog';
import { modifyServiceChargeSchema } from '../../../interface/room-posting-schema-interface';

interface FormValues {
  service_charge: number;
}

const FORoomPostingModifyLineForm = ({
  selectedIDs,
  withVat,
  withDisc,
  handleClose,
  appliedSC,
}: {
  selectedIDs: string;
  withVat: boolean;
  withDisc: boolean;
  appliedSC?: number;
  handleClose: () => void;
}) => {
  const { primary, textGray } = getColor();
  const { toast } = useToast();
  const [isFetching, setIsFetching] = useState(false);
  const { useProperty, ledgerRefetch } = useRoomPostingContext();
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const { openSecCodeDialog } = useAppContext();

  const methods = useForm<FormValues>({
    defaultValues: {
      service_charge: appliedSC || 0,
    },
    resolver: yupResolver(modifyServiceChargeSchema),
    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = methods;

  // API Transfer Folio
  const [postModifyServiceCharge] = usePostModifyServiceChargeMutation();

  useEffect(() => {
    setValue('service_charge', appliedSC || 0);
  }, [appliedSC, setValue]);

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    openSecCodeDialog(async (result) => {
      if (!result?.data.isValid) {
        onShowInfoDialog({
          desc: 'Invalid security code',
        });
        return;
      }
      setIsFetching(true);

      const { service_charge } = data;
      const scPercentage = service_charge / 100; // Convert to percentage

      const postServiceCharge: RoomPostingModifyServiceCharge = {
        properties: [
          {
            property: {
              propertyID: useProperty,
              trans_list: [
                {
                  identifier_list: selectedIDs,
                  serv_charge_perc: scPercentage,
                },
              ],
            },
          },
        ],
      };

      await postModifyServiceCharge(postServiceCharge)
        .unwrap()
        .then(() => {
          toast({
            severity: 'success',
            message: `Successfully modified service charge to ${service_charge}`,
          });
        })
        .catch((error) =>
          toast({
            severity: 'error',
            message: `Error occurred modify service charge to ${service_charge}: ${error}`,
          }),
        )
        .finally(() => {
          setIsFetching(false);
          ledgerRefetch();
          handleClose();
        });
    }, 'modVATSC');
  };

  //Remove Discount
  const [postRemoveDiscount] = usePostFolioRemoveDiscountMutation();

  const handleValidateRemoveDisc = () => {
    if (!withDisc) {
      onShowInfoDialog({
        desc: 'Unable to remove discount.\n\nSelected item(s) doesn`t have discounts',
      });

      return;
    }
    // show security code
    openSecCodeDialog(async (result) => {
      if (!result?.data.isValid) {
        onShowInfoDialog({
          desc: 'Invalid security code',
        });
        return;
      }
      handleApplyRemoveDisc();
    }, 'remove_disc');
  };

  const handleApplyRemoveDisc = async () => {
    setIsFetching(true);

    const postRemoveItemDisc: RoomPostingRemoveDiscZero = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            item_list: [
              {
                identifier_list: selectedIDs,
              },
            ],
          },
        },
      ],
    };

    await postRemoveDiscount(postRemoveItemDisc)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: `Successfully removed discount.`,
        });
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred remove discount item: ${error}`,
        }),
      )
      .finally(() => {
        setIsFetching(false);
        ledgerRefetch();
        handleClose();
      });
  };

  //Zero Rated
  const [postZeroRated] = usePostFolioZeroRatedMutation();

  const handleValidateZeroRated = () => {
    if (!withVat) {
      onShowInfoDialog({
        desc: 'Unable to apply VAT zero-rated.\n\nSelected item(s) doesn`t have vat.',
      });

      return;
    }

    openSecCodeDialog(async (result) => {
      if (!result?.data.isValid) {
        onShowInfoDialog({
          desc: 'Invalid security code',
        });
        return;
      }
      handleApplyZeroRated();
    }, 'modVATSC');
  };

  const handleApplyZeroRated = async () => {
    if (!withVat) {
      onShowInfoDialog({
        desc: 'Unable to apply VAT zero-rated !\n\nSelected item(s) doesn`t have vat.',
      });

      return;
    }
    //show confimation
    setIsFetching(true);

    const postZeroRatedItem: RoomPostingRemoveDiscZero = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            item_list: [
              {
                identifier_list: selectedIDs,
              },
            ],
          },
        },
      ],
    };

    await postZeroRated(postZeroRatedItem)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: `Successfully applied zero rate VAT.`,
        });
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred apply VAT zero rated item: ${error}`,
        }),
      )
      .finally(() => {
        setIsFetching(false);
        ledgerRefetch();
        handleClose();
      });
  };

  return (
    <FormProvider {...methods}>
      <Stack gap={2} sx={{ p: 3, width: '100%' }}>
        {isFetching && <LoadingComponent />}

        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' gap={2}>
            <Stack
              sx={{
                color: primary,
                backgroundColor: '#f7f7fb',
                width: '60px',
                height: '60px',
                borderRadius: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <PercentOutlined />
            </Stack>

            <ContainerTitle
              title='Modify Line'
              func={() => {}}
              titleStyle={{
                fontSize: '18px!important',
                fontWeight: '500',
              }}
            />
          </Stack>

          <IconButton
            id='modal-close-btn'
            color='inherit'
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        </Stack>

        <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
          {/* Service Charge */}
          <Typography
            sx={{
              mb: 2.5,
              borderBottom: '1px solid #aaa',
            }}
          >
            Customize Service Charge Rate of entry, 0-15%
          </Typography>

          <Stack direction='row' gap={1}>
            <InputFieldsController
              label='Service Charge'
              name='service_charge'
              control={control}
              errors={errors}
              InputProps={{
                endAdornment: <PercentOutlined />,
              }}
              sx={{
                width: '100%',
                '& input': { textAlign: 'right' },
              }}
            />
            <Button
              variant='contained'
              sx={{ height: '38px', width: '80px' }}
              type='submit'
            >
              Apply
            </Button>
          </Stack>

          {/* Zero Rated */}

          <Typography sx={{ mb: '15px', borderBottom: '1px solid #aaa' }}>
            Zero-Rated
          </Typography>
          <Stack direction='row' gap={1}>
            <Typography
              variant='body2'
              sx={{
                color: textGray,
                width: '100%',

                borderRadius: '5px',
                pl: '5px',
              }}
            >
              Change item(s) VAT type to Zero-Rated. If not sure, please refer
              to your Accounting Department
            </Typography>
            <Button
              variant='contained'
              sx={{ height: '38px', width: '80px' }}
              onClick={() => {
                handleValidateZeroRated();
              }}
            >
              Apply
            </Button>
          </Stack>

          {/* Remove Discount */}

          <Typography
            sx={{
              m: '15px 0px 15px 0px ',
              borderBottom: '1px solid #aaa',
            }}
          >
            Remove Discount
          </Typography>
          <Stack direction='row' gap={1}>
            <Typography
              variant='body2'
              sx={{
                color: textGray,
                width: '100%',

                borderRadius: '5px',
                pl: '5px',
                pb: '10px',
              }}
            >
              Removes recently-issued discounts to items. Uses item's VAT Type
              and Service Charge rate, if applicable
            </Typography>
            <Button
              variant='contained'
              sx={{ height: '38px', width: '80px' }}
              onClick={handleValidateRemoveDisc}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <DialogInformation />
    </FormProvider>
  );
};

export default memo(FORoomPostingModifyLineForm);
